<template>
  <div class="PricePill__Container">
    <p
      class="Price p4"
      data-ht-element="price"
      v-if="ready"
      :class="[isCart ? '--cart' : '', isProductCard ? '--product-card' : '']"
    >
      <span>
        <span
          class="Price__From"
          v-if="showFromCopy && !isSubtotal"
          :class="[showFromCopy ? '--show' : '']"
        >
          From:
        </span>
        <span
          class="Price__Default t4"
          v-if="showDefaultPrice"
          :class="{
            '--slashout': discountedPrice || (isCart && discountedPrice === 0),
            '--allAudiencesFalse':
              !allAudiences && isPDP && !isBundle && !isSubtotal,
          }"
        >
          {{ formattedPrice(formatMoneyCents(defaultPrice)) }}
        </span>
        <span
          v-if="showDiscountedPrice"
          class="Price__Discounted t4 js-price-control"
          :class="[!allAudiences && isPDP ? '--allAudiencesFalse' : '']"
        >
          {{ formattedPrice(formatMoneyCents(discountedPrice)) }}
        </span>
        <span
          v-if="meetsTestingCriteria"
          class="Price__Discounted t4 js-price-variant"
          :class="[!allAudiences && isPDP ? '--allAudiencesFalse' : '']"
        >
          {{ formatTestRounding(discountedPrice) }}
        </span>
      </span>
    </p>
    <div v-else class="Price Stencil Stencil--noMinHeight">
      <div
        class="Stencil-Price"
        :class="[isCart ? '--cart' : '', isProductCard ? '--product-card' : '']"
      ></div>
    </div>
    <PromotionalPills
      :ready="ready"
      :tags="productTags"
      :promo="promo"
      :location="location"
      :promotedOptionGroups="promotedOptionGroups"
      :discountedPrice="discountedPrice"
      :defaultPrice="defaultPrice"
    />
  </div>
  <PriceMessage
    v-if="showMessaging && ready"
    :item="selectedProduct"
    :currentStep="currentStep"
    :defaultPrice="defaultPrice"
    :promo="promo"
  />
</template>

<script setup>
import {
  formatMoneyCents,
  calculateSWD,
  getSubtotal,
  formattedPrice,
  calcBMSMSavings,
} from '@utils/price-helpers';
import { parseTag } from '@utils/format';
import { watch, ref, computed, onMounted } from 'vue';
import PriceMessage from './PriceMessage.vue';
import PromotionalPills from './PromotionalPills.vue';
import { useCartStore } from '../Cart/Composables/Store';
import { getActivePinia } from 'pinia';

const store = async () => {
  await getActivePinia();
  return useCartStore();
};

/**************************
 * Table of Contents
 *
    P1: Props / Component Data Structure
        P1A: Product Tags
    P2: Variables
        P2A: Google Shopping, Galopt Parameters
        P2B: Location Specific Variables
        P2C: SWD and Tiered Discount Related Variables
        P2D: Compare Pricing Related Variables
        P2E: Product Bundle Specific Variables
    P3: Pricing Logic and Calculations
        P3A: Default Price Logic
        P3B: Discounted Price Logic
    P4: Helper Functions
    P5: Error Debugging Functions
    P6: POGs
 **************************/

/************
 * P1: Props / Component Data Structure
 ************/
const props = defineProps({
  cart: { type: Array, default: false },
  item: { type: Object },
  components: { type: Object, default: false },
  isComponent: { type: Boolean, required: false },
  variant: { type: [Object, Number], required: false },
  shellComponent: { type: Object, required: false },
  currentStep: { type: Number, required: false },
  location: { type: String },
  showMessaging: { type: Boolean, required: false },
  ready: { type: Boolean, default: false },
  edit: { type: Boolean, default: false },
  selections: { type: Array, default: [] },
  promotedOptionGroups: { type: Array, default: [] },
  selectedProduct: { type: [Object, Boolean], default: false },
});

const promo = ref(false);
const prices = ref({
  variantPrice: false,
  tprComponentPrice: props?.item?.component_price ?? false,
  minVariantPrice:
    props?.item?.priceRange?.minVariantPrice?.amount * 100 ?? false,
  fromPrice: false,
  fromComparePrice: false,
  cartPrice: false,
  componentPrice: [],
});

const tags = props.item?.tags ?? [];
const bundleCompPrices = ref({
  price: 0,
  discountedPrice: 0,
});

/***
P1A: Product Tags
 ***/
const productTags = computed(() => {
  // For stepped/auto bundles you need to depend on the parent product tags
  // These data is structured differently in stepped/cart bundles hence the if logic
  const bundleSum = props.currentStep >= 1;
  const cartComponents = props?.item?.is_component;

  if (bundleSum || (props.edit && props.location === 'pdp')) {
    return props.shellComponent.tags;
  } else if (cartComponents) {
    return props.item.shell_tags;
  } else if (props.selectedProduct) {
    return props.selectedProduct.tags;
  } else {
    return tags;
  }
});

/************
 * P2: Variables
 ************/

/***
P2A: Google Shopping, Galopt Parameters
***/
const googleShopping = window.location.href.includes('/collections/gs');
const allAudiences = computed(() => {
  // We only need this logic for the price that are in ads in PDPs that are using the /gs url
  const audiences = promo.value.swd.allAudiences;
  const adUrl = isPDP.value && googleShopping;

  // We still want the slashed out discount to appear in the cart, and product slider, we only want to hide the price in the PDP when all Audiences is false
  const excludedLocations = !isProductCard.value && !isCart.value;
  // If there is an active category sale we do need to show the discounted price there always, otherwise it will also cause price mismatches..
  const pdpCategorySale = isCompare.value && isPDP.value;

  if (adUrl && excludedLocations && !pdpCategorySale) {
    return audiences;
  }

  // Otherwise for all other price component cases return true
  return true;
});

const showFromCopy = computed(() => {
  // Do not show in cart or when all audiences is false
  if (isCart.value || isUpsell.value) return false;
  // Do not show for free items
  if (defaultPrice.value < 0 || typeof defaultPrice.value !== 'number')
    return false;

  const noVariantSelected = !prices.value.variantPrice;
  const noVariantStepped = typeof props?.variant === 'number';

  if (isBundle.value && !isAuto.value && !isProductCard.value) {
    return noVariantSelected && noVariantStepped;
  }

  return noVariantSelected;
});

/***
P2B: Location Specific Variables
***/

const isSubtotal = computed(() => props.location === 'subtotal');

const categoryDiscount = computed(() => {
  const categoryDiscount =
    parseTag(productTags.value, 'Category Promo') ?? false;
  const categoryEnabled = promo?.value?.category?.enabled ?? false;

  return categoryDiscount && categoryEnabled ? categoryDiscount : 0;
});
const isQV = computed(() => props.location === 'quick-view');
const isAuto = computed(
  () =>
    props.location === 'auto-bundle-modal' ||
    (productTags.value.some((tag) => tag.includes('Auto-Bundle')) &&
      !isQV.value)
);
const isBundleStep = computed(
  () => props.location === 'bundle-step' && !isQV.value
);
const isSteppedReview = computed(
  () => props.location === 'stepped-bundle-review-sum' && !isQV.value
);
const isCart = computed(() => props.location === 'cart');
const isUpsell = computed(
  () => props.location === 'upsell' || props.location.includes('upsell')
);
const isCartBundle = computed(
  () => props?.item?.is_bundle || props?.item?.is_component
);
const isPDP = computed(() => window.location.href.includes('/products'));
const isBundle = computed(() => {
  // Cart endpoint is structured differently + Bundle checkout product does not contain 'Product Bundle' tag
  if (isCart.value && isCartBundle.value) return true;
  return (
    productTags.value.some((tag) => tag.includes('Product Bundle')) &&
    !isQV.value
  );
});

const isProductCard = computed(() => props.location === 'product-card');

/***
P2C: SWD and Tiered Discount Related Variables
***/

const promoDiscountQualified = computed(() => {
  if (
    !promo.value ||
    (!promo?.value?.swd?.enabled && !promo.value.tiers?.enabled)
  )
    return false;

  const promoTags = promo.value.tiers?.enabled
    ? promo.value.tiers?.tags?.split(',')
    : promo.value.swd.tags.split(',');
  const multipleTags = promo.value.swd.multipleTags; // requires for multiple tag values to be true. ex: Slashouts Default & Discount Allow

  let match = productTags.value.filter((value) => promoTags.includes(value));

  if (!match) return false;
  if (match?.length >= 2 && multipleTags) {
    return true;
  } else if (match?.length >= 1) {
    return true;
  } else if (props?.location == 'subtotal' && promoTags?.length >= 1) {
    return true;
  }

  return false;
});

const showTieredPricing = computed(() => {
  return (
    promo.value.tiers?.enabled &&
    (isSubtotal.value || (promoDiscountQualified.value && isCart.value))
  );
});

//an object to pass into calculateSWD to determine which discount to take from the promo object
const promoQualified = computed(() => {
  return {
    swd: promoDiscountQualified.value ?? false,
    tiered: showTieredPricing.value ?? false,
    category: promo?.value?.category?.enabled ?? false,
  };
});

/***
P2D: Compare Pricing Related Variables
 ***/
const isCompareCart = computed(() => {
  const cartVariantPrice = props.item?.compareAtPrice;
  if (cartVariantPrice > 1) {
    prices.value.variantComparePrice = cartVariantPrice;
    return (prices.value.fromComparePrice =
      props.item.compareAtPrice * props.item.quantity);
  }
});

// Compare price will be used for category promos or last call products
const isCompare = computed(() => {
  if (isCart.value) return isCompareCart.value;
  const variantPrice = props.variant?.price?.amount * 100 || false;
  const defaultVariantIndex = getVariantIndex() >= 0 ? getVariantIndex() : 0;

  // TODO We need to add a more specific stepped bundle review check for this instead of isBundle
  if (
    !props.variant?.compareAtPrice &&
    props?.location === 'stepped-review-component'
  ) {
    return 0;
  }

  // Sets default when no selection is made
  const defaultPrice =
    props.item?.variants[defaultVariantIndex]?.price?.amount * 100;
  const defaultFromCompare =
    props.item?.variants[defaultVariantIndex]?.compareAtPrice?.amount * 100 ||
    false;

  // When a selection has been made
  let comparePrice;
  if (props.variant?.compareAtPrice?.amount) {
    comparePrice = props.variant?.compareAtPrice?.amount * 100;
  } else if (props.variant?.comparePrice) {
    comparePrice = props.variant?.comparePrice;
  } else if (defaultFromCompare) {
    comparePrice = defaultFromCompare;
  }

  prices.value.variantComparePrice = variantPrice ? variantPrice : defaultPrice;

  return (prices.value.fromComparePrice = comparePrice);
});

const fromPrice = computed(() => {
  // ACF Metafield price can come through as number or JSON, so need to parse.
  if (props.item?.fromPrice?.value) {
    prices.value.fromPrice = parseFloat(
      JSON.parse(props.item?.fromPrice.value)
    );
  }

  // if fromPrice metafield is blank, we use min variant price as fallback
  return prices.value.fromPrice
    ? prices.value.fromPrice
    : prices.value.minVariantPrice;
});

/***
P2E: Product Bundle Specific Variables
***/

const isBundleCompSum = computed(
  () => isSteppedReview.value || (isAuto.value && props.edit !== true)
);

const hasBundleSelections = computed(() => props?.item?.components?.length > 0);

const bundleSum = computed(() => {
  // For stepped and autobundles, you need to add the prices of each componnet together to show the total price
  if (isBundleCompSum.value || props?.item?.components?.length > 0) {
    bundleCompPrices.value.price = 0;
    bundleCompPrices.value.discountedPrice = 0;

    const componentsArray = props?.item?.components;
    const prices = componentsArray?.reduce((sum, product) => {
      const componentDiscountedPrice =
        product.discountedPrice || product.fromPrice?.value;
      let componentPrice =
        (product.component_price || product.price) -
        product?.component_savings * 100;

      const cachedPrice =
        product.line_level_total_discount == 0
          ? calculateSWD(componentDiscountedPrice, promoQualified, props?.cart)
          : calculateSWD(componentPrice, promoQualified, props?.cart);

      sum.value.price += parseInt(componentPrice);
      sum.value.discountedPrice += Math.round(
        Math.abs(cachedPrice / 100) * 100
      );
      return sum;
    }, bundleCompPrices);

    return prices.discountedPrice;
  }
});

const noBundleDiscount = computed(() =>
  productTags.value.some((tag) => tag.includes('Product Bundle No Discount'))
);

/************
 P3: Pricing Logic / Calculations
 ************/

// unless the price is shown in the cart, the price shown is based off of a quantity of 1
const quantity = isCart.value ? props?.item.quantity : 1;

/***
 P3A: Default Price, Strikethrough Price (when discounted price is present)
 ***/

const showDefaultPrice = computed(() => {
  const noVariantSelected = !prices.variantPrice;
  const noFreeItem = defaultPrice?.value > 0;
  const priceVariation =
    defaultPrice?.value > discountedPrice.value || !discountedPrice.value;
  return noVariantSelected && noFreeItem && priceVariation;
});

const defaultPrice = computed(() => {
  // If Variant isnt selected we use from price metafields
  const selectedPrice = props.variant?.price?.amount * 100;
  prices.value.variantPrice = selectedPrice;

  const originalPrice =
    prices.value.variantPrice ||
    prices.value.tprComponentPrice ||
    fromPrice.value;

  switch (true) {
    case isSubtotal.value &&
      getSubtotal(props?.cart, true, promoQualified) >
        getSubtotal(props?.cart, false, promoQualified):
      return getSubtotal(props?.cart, true, promoQualified);
    // Excludes free items
    case prices.value.variantPrice <= 0:
      priceDebugMsg(prices.value.variantPrice, 100);
      return 0;
    // PDP Bundle Component Sum
    case props?.components?.length > 0 &&
      props?.location != 'auto-bundle-modal' &&
      props?.location != 'bundle-step' &&
      props?.location != 'quick-view':
      const components = Object.values(props.components);
      priceDebugMsg(addComponentPrices(components, true), 111);
      return addComponentPrices(components, true);
    // Stepped Bundle Price
    case isBundleStep.value && props?.currentStep === 0:
      priceDebugMsg(fromPrice.value, 103);
      return fromPrice.value;
    case props?.isComponent:
      const basePrice =
        props?.variant?.compareAtPrice?.amount || props?.variant?.price?.amount;
      const cartPrice =
        props?.item?.compareAtPrice || props?.item?.component_price;
      priceDebugMsg(props?.variant?.price?.amount, 110);
      return (cartPrice || parseFloat(basePrice * 100)) * quantity;
    // When compare at price is used ex: category, temporary reduction, last call
    case isCompare.value > 1 && props.variant !== false:
      priceDebugMsg(isCompare.value, 104);
      return isCompare.value;

    // Original Price has several use cases so its use case will run for
    // from price, when a variant is selected, cart price, and component cart price
    case originalPrice >= 1:
      priceDebugMsg(originalPrice, 105);
      return originalPrice * quantity;
  }
});

/***
 P3B: Discounted Price
 ***/

const meetsTestingCriteria = computed(() => {
  const location = props?.location ?? '';
  const eligibleLocation = location === 'product-card';
  return showDiscountedPrice.value && eligibleLocation;
});

const showDiscountedPrice = computed(() => {
  const prospectGsUrl = googleShopping && !allAudiences.value;

  if (prospectGsUrl && !isBundle.value && !isSubtotal.value) return false;

  if (discountedPrice.value || (isCart && discountedPrice === 0)) {
    return true;
  }
  if (
    isSubtotal.value &&
    getSubtotal(props?.cart, true, promoQualified) >
      getSubtotal(props?.cart, false, promoQualified)
  ) {
    return true;
  }
});

const discountedPrice = computed(() => {
  if (defaultPrice?.value <= 0 && !isCart) return; // return for free items
  if (noBundleDiscount.value) return; // Bundle does not qualify for discount

  const swdPrice = calculateSWD(
    defaultPrice?.value,
    promoQualified,
    props?.cart
  );
  let bundleDiscDefault = bundleDiscount(defaultPrice?.value);

  const prospectGsUrl = googleShopping && !allAudiences.value;

  switch (true) {
    case isSubtotal.value:
      return getSubtotal(props?.cart, false, promoQualified);
    // Free item in cart
    case isCart.value && props.item.price === 0:
      priceDebugMsg(props?.item?.price, 1);
      return props?.item?.price * quantity;

    case prospectGsUrl:
      priceDebugMsg(prices.value.bundleDiscPrice, 14);
      if (categoryDiscount.value) {
        prices.value.bundleDiscPrice *=
          (100 - parseInt(categoryDiscount.value)) / 100;
      }

      return prices.value.bundleDiscPrice;
    // Bundle Discount and Compare
    case isBundle.value &&
      isCompare.value > 1 &&
      !isSteppedReview.value &&
      !isBundleCompSum.value:
      //check if component price should read from the tpr component price, or the variant compare price
      const bundleComponentPrice =
        prices.value.tprComponentPrice || prices.value.variantComparePrice;
      let bundleCompare = bundleDiscount(bundleComponentPrice);
      //if the bundle is also applicable for a SWD, apply it after the bundle and TPR discounts have taken place

      bundleCompare =
        calculateSWD(bundleCompare, promoQualified, props?.cart) ||
        bundleCompare;
      priceDebugMsg(bundleCompare, 8);
      return bundleCompare * quantity;
    //Stepped Bundle price
    case isBundleStep.value || isSteppedReview.value:
      let baseSteppedBundlePrice;
      if (isBundleStep.value) {
        baseSteppedBundlePrice =
          props?.variant?.price?.amount * 100 || fromPrice.value;
      } else {
        baseSteppedBundlePrice =
          fromPrice.value || props?.variant?.price?.amount * 100;
      }

      if (props?.components?.length > 1 && isSteppedReview.value) {
        baseSteppedBundlePrice = addComponentPrices(
          Object.values(props.components)
        );
      }
      const steppedBundlePrice = calculateSWD(
        bundleDiscount(baseSteppedBundlePrice),
        promoQualified
      );

      priceDebugMsg(steppedBundlePrice, 10);
      return steppedBundlePrice;
    // PDP Bundle Component Sum
    case (isBundleCompSum.value && hasBundleSelections.value) ||
      props?.item?.components?.length > 0:
      bundleSum.value;
      priceDebugMsg(bundleCompPrices.value.discountedPrice, 5);
      const bundleDiscPrice = Math.min(
        prices?.value?.bundleDiscPrice,
        prices.value?.fromPrice
      );
      const bundleCompPrice = showTieredPricing.value
        ? calculateSWD(
            props?.item?.fromPrice?.value || bundleDiscPrice,
            promoQualified,
            props?.cart
          )
        : addComponentPrices(Object.values(props?.item?.components));
      return bundleCompPrice * quantity;

    //bundle component, with or without SWD
    case props?.isComponent:
      const bundleDiscountedPrice =
        props?.item?.price ||
        parseInt(bundleDiscount(props?.variant?.price?.amount * 100));

      //check if the price calculations have already been applied via Shopify. Because Shopify rounds its line_level_total_discount property, the prices are off by a cent or two.
      //If the current price Shopify gives us is the same (within one or two cents) as the calculated price, we will use the current price to match checkout.
      const savingsApplied =
        Math.abs(
          bundleDiscountedPrice -
            calculateSWD(
              props?.item?.component_price -
                props?.item?.component_savings * 100,
              promoQualified,
              props?.cart
            )
        ) <= 2;
      priceDebugMsg(
        calculateSWD(
          Math.round(bundleDiscountedPrice),
          promoQualified,
          props?.cart
        ) * quantity,
        14
      );
      return savingsApplied
        ? props?.item?.price * quantity
        : calculateSWD(
            Math.round(bundleDiscountedPrice),
            promoQualified,
            props?.cart
          ) * quantity;
    //return the sum of all components in a bundle
    case props?.components?.length > 0 &&
      props?.location != 'auto-bundle-modal' &&
      props.location != 'quick-view':
      const components = Object.values(props.components);
      const componentSum = addComponentPrices(components);

      return calculateSWD(
        bundleDiscount(componentSum),
        promoQualified,
        props?.cart
      );

    // Bundle and SWD
    case isBundle.value && swdPrice > 0 && !isQV.value:
      const bundleSwd = calculateSWD(
        prices.value.bundleDiscPrice,
        promoQualified,
        props?.cart
      );
      prices.value.bundleSwdApplied = bundleSwd;

      if (
        categoryDiscount.value &&
        props.location != 'stepped-review-component'
      ) {
        prices.value.bundleSwdApplied *=
          (100 - parseInt(categoryDiscount.value)) / 100;
      }
      priceDebugMsg(prices.value.bundleSwdApplied, 7);
      return calculateSWD(prices.value.bundleSwdApplied);
    case isBundle.value:
      let bundlePrice = prices.value.tprComponentPrice
        ? bundleDiscount(prices.value.tprComponentPrice)
        : prices.value.bundleDiscPrice;
      priceDebugMsg(bundleDiscDefault, 9);
      return bundlePrice * quantity;
    // Single SKU Compare Pricing (with or without SWD)
    case isCompare.value > 1:
      const comparePrice = isCart.value
        ? props?.item?.price * quantity + props?.item?.line_level_total_discount
        : prices.value.variantComparePrice;

      priceDebugMsg(props?.item?.price, 11);
      //calculateSWD(props?.item?.price, promoQualified, props?.cart) * quantity
      return calculateSWD(comparePrice, promoQualified, props?.cart);

    // BMSM (Buy More Save more and SWD)
    case props?.item?.bmsm_enabled && swdPrice > 0:
      //with BMSM and SWDs, the line_level_total_discount adds both BMSM and SWD discounts to the value which throws off the usual caching fix of simply adding line_level_total_discount back to the price property.
      //as a result, we must add logic to check if we need to apply SWDs when coming back to cart from checkout.
      const bmsmDiscount = calcBMSMSavings(props?.item);
      const bmsmPrice = calculateSWD(
        parseInt(props?.item?.fromPrice.value) * bmsmDiscount,
        promoQualified,
        props?.cart
      );
      priceDebugMsg(bmsmPrice, 12);
      return bmsmPrice * quantity;
    // SWD Only
    case swdPrice > 0:
      const cachedPrice = isCart.value
        ? calculateSWD(
            props?.item?.price * quantity +
              props?.item?.line_level_total_discount,
            promoQualified,
            props?.cart
          )
        : false;
      priceDebugMsg(prices.value.swdPrice, 13);
      const pdpPrice =
        props?.variant?.price?.amount * 100 ||
        parseInt(props?.item?.fromPrice?.value);

      return cachedPrice || calculateSWD(pdpPrice, promoQualified);
  }
});

/************
 P4: Helper Functions
 ************/
const getVariantIndex = () => {
  const variants = props.item?.variants ?? [];

  if (variants?.length <= 0) return 0;

  const url = new URL(window.location);
  const selectedVariant = url.searchParams.get('variant') ?? false;
  if (!selectedVariant) return 0;

  return variants.findIndex((variant) => variant.id.includes(selectedVariant));
};

const bundleDiscount = (initialPrice) => {
  // If there is no bundle discount return nothing, this happens during collabs
  if (noBundleDiscount.value) return;
  if (isBundle.value) {
    const discount =
      productTags.value
        .filter((tag) => tag.includes('Discount Amount'))
        .map((tag) => parseInt(tag.split(':')[1]))[0] / 100;
    const discountAmount = 1 - discount;
    const price = initialPrice * discountAmount;

    return (prices.value.bundleDiscPrice = price);
  }
};

const addComponentPrices = (components, defaultPrice) => {
  let sum = 0;
  components.forEach((component) => {
    let basePrice;
    if (defaultPrice) {
      if (component?.variantBySelectedOptions) {
        basePrice =
          component?.variantBySelectedOptions?.compareAtPrice?.amount ||
          component?.variantBySelectedOptions?.price?.amount;
      } else {
        basePrice =
          (component?.compareAtPrice ||
            component?.component_price ||
            component?.price) / 100;
      }
    } else {
      basePrice =
        component?.variantBySelectedOptions?.price?.amount ||
        Math.round(component.price) / 100;

      if (
        !component?.variantBySelectedOptions &&
        component?.line_level_total_discount == 0
      ) {
        basePrice = calculateSWD(basePrice, promoQualified, props?.cart);
      }
    }

    sum +=
      parseFloat(basePrice) ||
      Math.round(component?.component_price) / 100 ||
      0;
  });
  return sum * 100;
};

const formatTestRounding = (amount) => {
  const parsedPrice = formatMoneyCents(amount);
  const roundedPrice = Math.round(parsedPrice);
  return formattedPrice(roundedPrice);
};

/************
 P5: Error Debugging Functions
 ************/

//by adding the _bl_dev__price_debugger cookie to your URL, you will enable debug mode for the price component
if (document.cookie.indexOf('_bl_dev__price_debugger') != -1) {
  window.Brooklinen.dev.price_debug = true;
}

//in price component debug mode, you will get a bunch of information pertaining to the price. most importantly, where in the switch case you're being routed to.
const priceDebugMsg = (price, index) => {
  if (window.Brooklinen.dev.price_debug) {
    console.log(`${props.item.title} (${props.location})`, {
      displayed_price: price,
      error_index: index,
      prices: prices.value,
      props: props,
    });
  }
};

/************
 P6: POGs
 ************/
const promotedOptionGroups = computed(() => {
  const data = {};
  data.selections = props?.selections;
  data.groups = props?.promotedOptionGroups;
  return data;
});

watch(
  () => props.ready,
  () => {
    promo.value = window.Brooklinen.promo.discounts;
  },
  { immediate: true }
);
</script>
